import { BBModel, StorySymbols } from 'types/story';
import { isSymbol } from 'common/utils/blocks/symbol';
import { componentWalk } from 'common/utils/blocks/component-walk';
import { getSymbolModel } from 'common/utils/blocks/get-symbol-model';

/**
 * @callback callback
 * @param {CallbackProps} props
 */

/**
 * @typedef CallbackProps
 * @property {object} component - component object model
 * @property {number} index     - component index among parent children
 * @property {string} path      - dot-notated path to component from components root array
 */

/**
 * Walk components depth-first and call a callback on each,
 * Every component, even if it's a symbol, will be represented as fully merged with master & instance
 */
export function componentWalkFull({
	elements,
	symbols,
	callback,
	initialPath,
}: {
	elements: BBModel[];
	symbols: StorySymbols;
	initialPath?: string;
	callback: (p: {
		component: BBModel;
		index: number;
		path: string;
		parent?: BBModel;
		// Stop main componentWalk
		stopAll: () => void;
		// Stop current walk callback recursion
		stopCurrent: () => void;
	}) => void;
}) {
	const stop = {
		all: false,
		current: false,
		stopCurrent: () => {
			stop.current = true;
		},
		stopAll: () => {
			stop.all = true;
		},
	};

	componentWalk<BBModel>(
		elements,
		function walk({ component, index, path, parent }) {
			if (isSymbol(component) && symbols && !component._id) {
				const fullComponent = getSymbolModel(component, symbols);
				walk({ component: fullComponent, path, index, parent });
				return;
			}

			stop.current = false;

			callback({ component, index, path, parent, stopCurrent: stop.stopCurrent, stopAll: stop.stopAll });

			if (!stop.all && !stop.current) componentWalk<BBModel>(component.children, walk, path, component);
		},
		initialPath
	);
}
